/**
 * Canonical browser — grouped list of validated mappings per canonical.
 *
 * Leans on the resolver /options endpoint which already aggregates validated
 * state/court mappings + legacy/json fallbacks. Uses the current state +
 * practice area filters.
 */
import React, { useEffect, useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { listOptions, type SubtypeOption } from './caseSubtypeTaxonomyApi';

interface Props {
  stateCode: string;
  practiceArea: string;
}

const CanonicalBrowser: React.FC<Props> = ({ stateCode, practiceArea }) => {
  const [options, setOptions] = useState<SubtypeOption[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!practiceArea) {
      setOptions(null);
      return () => { cancelled = true; };
    }
    setLoading(true);
    setError(null);
    listOptions({
      practice_area: practiceArea,
      state_code: stateCode || undefined,
    })
      .then((r) => { if (!cancelled) setOptions(r.options); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load canonicals'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [stateCode, practiceArea]);

  if (!practiceArea) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900/30 p-6 text-sm text-gray-500 dark:text-gray-400 flex items-start gap-2.5">
        <InformationCircleIcon className="h-5 w-5 mt-0.5 shrink-0" />
        <div>
          <div className="font-medium text-gray-700 dark:text-gray-300">
            Pick a practice area
          </div>
          <div className="mt-0.5">
            The canonical browser is scoped to one practice area at a time. Use the filter above.
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-violet-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-lg p-3 text-sm">
        {error}
      </div>
    );
  }

  if (!options || options.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
        No canonicals resolved for {practiceArea}
        {stateCode ? ` in ${stateCode}` : ''}.
      </div>
    );
  }

  return (
    <div className="space-y-2" style={{ animation: 'fadeSlideIn 300ms ease-out forwards' }}>
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Resolved options for <span className="font-semibold text-gray-700 dark:text-gray-200">{practiceArea}</span>
        {stateCode ? <> in <span className="font-semibold text-gray-700 dark:text-gray-200">{stateCode}</span></> : null}.
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2 text-left">Canonical</th>
              <th className="px-3 py-2 text-left">Label</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-right">Forms</th>
              <th className="px-3 py-2 text-left">Template</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {options.map((o) => (
              <tr key={`${o.canonical_key}-${o.mapping_id || 'x'}`} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-200">
                  {o.canonical_key}
                </td>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                  <div className="font-medium">{o.label}</div>
                  {o.local_key && o.local_key !== o.canonical_key && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                      alias: {o.local_key}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  <SourceBadge scope={o.source_scope} status={o.resolution_status} />
                </td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-200">
                  {o.form_count ?? 0}
                </td>
                <td className="px-3 py-2 text-xs">
                  {o.template_id ? (
                    <span className="text-emerald-700 dark:text-emerald-300">
                      {o.template_status || 'present'}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SOURCE_COLORS: Record<string, string> = {
  court: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200',
  state: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200',
  national: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200',
  legacy: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  json: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const SourceBadge: React.FC<{
  scope?: string;
  status?: string;
}> = ({ scope, status }) => {
  const s = scope || 'json';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${SOURCE_COLORS[s] || SOURCE_COLORS.json}`}>
      {s}
      {status && status !== 'validated' ? ` · ${status}` : ''}
    </span>
  );
};

export default CanonicalBrowser;
